import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiSearch, FiArrowLeft,
  FiCheck, FiXCircle, FiEye, FiFileText, FiPrinter, FiTruck, FiLink
} from 'react-icons/fi';
import { colors } from '../../theme/colors';
import LinkedDocNumbers, { parentLinks } from '../../components/LinkedDocNumbers';
import {
  collection, getDocs, query, orderBy, where, doc, updateDoc, getDoc
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getItemsCached, getCustomersCached, getEmployeesCached, getJobsCached, getLocationsCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import { missingLocation, reqLabel, LOCATION_REQUIRED_MSG } from '../../utils/locationRequired';
import { formatAmount } from '../../utils/documentNumbering';
import { printDocument, loadDocumentFormats } from '../../utils/printTemplates';
import {
  createTransaction, updateTransaction, deleteTransaction,
  getTransactions, getTransaction, updateTransactionStatus
} from '../../utils/transactionHelper';
import { deleteStockFlow, getAvailableBatches, getAvailableSerials, getItemStockCached, invalidateItemStock } from '../../utils/stockFlowHelper';
import LineUnitPicker from '../../components/LineUnitPicker';
import TwoUnitQtyBox, { TwoUnitNote } from '../../components/TwoUnitQtyBox';
import { seedLineUnitFromItem, applyUnitChangeToLine, twoUnitSwitchQty } from '../../utils/unitConversion';
import { loadIfEnabled as loadCustomerPricing, EMPTY_PRICING, filterItemsByPricing, getOverridePrice } from '../../utils/customerPricingHelper';
import { recomputeSizeLine } from '../../utils/sizeHelper';
import PageLayout from '../../components/PageLayout';
import SendWhatsAppButton from '../../components/SendWhatsAppButton';
import CustomerSelect from '../../components/CustomerSelect';
import ItemSelect from '../../components/ItemSelect';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useTableSort from '../../hooks/useTableSort';
import useDocListPagination from '../../hooks/useDocListPagination';
import DocListPagination from '../../components/DocListPagination';
import SerialSelectPopover from '../../components/SerialSelectPopover';
import { checkStockAvailability, isFutureDate, toBaseQty, writeAuditLog } from '../../utils/validationHelpers';
import AttachmentPanel from '../../components/AttachmentPanel';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton, LoadingMessage,
  TranEmptyState, TableContainer, Table, Th, Tr, Td, StatusBadge, ActionGroup, IconBtn,
  SummaryBar, FormHeader, BackButton, FormTitle, FormActions, SaveButton, FormCard,
  SectionTitle, FormGrid, FormGroup, Label, Input, Select, TextArea, InfoGrid, InfoItem,
  NotesText, LineItemsHeader, AddLineButton, LineTableContainer, LineTable, LineTh, LineTd,
  LineInput, RemoveLineBtn, TotalsSection, TotalsTable, TotalRow, LinkedBadgeContainer,
  LinkedDocsSection, SortableTh, SortContext
} from '../../components/SharedStyles';
import SizeLineRow from '../../components/SizeLineRow';
import { prewarmSave } from '../../utils/transactionHelper';
import { inActiveBranch } from '../../utils/branchLink';
import { carryEmployeeJob, hsCodePatch, customerPOOf } from '../../utils/linkCarry';

const TRAN_TYPE_CODE = 'DC';

// Footer summary spec — aggregated server-side in paged mode (~few reads),
// computed from the loaded list in filtered mode. Matches the old footer.
const SUMMARY_SPEC = {
  statuses: ['Draft', 'Dispatched', 'Delivered', 'Invoiced']
};

const EMPTY_LINE = {
  itemId: '', description: '', orderedQty: 0, quantity: 1, unitOfMeasure: '', unitCode: '',
  unitPrice: 0, unitCost: 0, lineTotal: 0,
  // Carried through SO → DC → Invoice so line discount % + tax survive the chain.
  // The DC itself is a dispatch note and doesn't display these; they just ride along.
  discountPercent: 0, discountAmount: 0, taxable: false, taxRate: 0, taxAmount: 0,
  trackingType: 'None', batchNumber: '', serialNumbers: [],
  batch: '', packSize: ''
};

const STATUS_COLORS = {
  Draft: colors.warning.main, Dispatched: colors.info.main,
  Delivered: colors.success.main, Invoiced: '#9C27B0',
};

// ── Sale Order delivery progress (FLW-03 / FLW-06) ──────────────────────────
// An SO is delivered by its active Delivery Challans (not Draft / Cancelled) and
// by posted invoices made straight from it. Each challan / invoice line keeps the
// SO line it came from (soLineNumber = that line's number on the SO); older lines
// without it fall back to the same item (or description).
const DC_ACTIVE = (s) => !!s && s !== 'Draft' && s !== 'Cancelled';
const INVOICE_LIVE = ['Posted', 'Partial', 'Paid'];
// 'Delivered' is an old, invalid SO status — it is corrected on the next recalc.
const SO_PROGRESS_STATUSES = ['Approved', 'Partially Delivered', 'Delivered', 'Completed'];
const r6 = (n) => Math.round((Number(n) || 0) * 1e6) / 1e6;
const soLineNo = (sl, idx) => Number(sl?.lineNumber) || (idx + 1);

const findSOLineIndex = (soLines, delivered, ref, itemId, desc, qty, removing) => {
  const n = Number(ref) || 0;
  if (n > 0) {
    const i = soLines.findIndex((sl, idx) => soLineNo(sl, idx) === n);
    if (i >= 0 && (!itemId || !soLines[i].itemId || soLines[i].itemId === itemId)) return i;
  }
  const same = [];
  soLines.forEach((sl, idx) => {
    if (itemId ? sl.itemId === itemId : (!!desc && !sl.itemId && sl.description === desc)) same.push(idx);
  });
  const fit = same.find(idx => (removing
    ? (delivered[idx] || 0) >= qty
    : (Number(soLines[idx].quantity) || 0) - (delivered[idx] || 0) > 0));
  return fit !== undefined ? fit : (same.length ? same[0] : -1);
};

const countsOnSO = (d) => (d.tranTypeCode === 'DC'
  ? DC_ACTIVE(d.status)
  : (['SI', 'NSI'].includes(d.tranTypeCode) && INVOICE_LIVE.includes(d.status)));

// Delivered qty per SO line from the SO's child documents (skipId = leave one out).
// Lines that carry their SO line number are applied first, older lines without
// it then fill whatever is still open on a line of the same item.
export const soDeliveredFromChildren = (soLines, children, skipId) => {
  const delivered = (soLines || []).map(() => 0);
  const live = (children || []).filter(c => c && c.id !== skipId && countsOnSO(c));
  const add = (l) => {
    const qty = Number(l.quantity) || 0;
    if (!(qty > 0)) return;
    const i = findSOLineIndex(soLines, delivered, l.soLineNumber, l.itemId || '', l.description || '', qty, false);
    if (i >= 0) delivered[i] = r6(delivered[i] + qty);
  };
  live.forEach(c => (c.lines || []).forEach(l => { if (Number(l.soLineNumber) > 0) add(l); }));
  live.forEach(c => (c.lines || []).forEach(l => { if (!(Number(l.soLineNumber) > 0)) add(l); }));
  return delivered;
};

// SO line number each document line belongs to (tags older lines saved without
// it). `progress` (from loadSOProgress) = what OTHER documents already delivered,
// so an old line goes to the SO line that still has room for it.
export const tagSOLineNumbers = (soLines, docLines, progress = null) => {
  const used = (soLines || []).map((sl, i) => {
    const p = progress && progress[soLineNo(sl, i)];
    return p ? (Number(p.delivered) || 0) : 0;
  });
  return (docLines || []).map(l => {
    const qty = Number(l.quantity) || 0;
    const i = findSOLineIndex(soLines, used, l.soLineNumber, l.itemId || '', l.description || '', qty, false);
    if (i < 0) return 0;
    used[i] = r6(used[i] + qty);
    return soLineNo(soLines[i], i);
  });
};

// Approved (nothing delivered) / Partially Delivered / Completed (all delivered).
// A Draft or Cancelled SO keeps its status.
export const soStatusFromLines = (lines, current) => {
  if (!SO_PROGRESS_STATUSES.includes(current)) return current;
  const all = lines.length > 0 && lines.every(l => (Number(l.deliveredQty) || 0) >= (Number(l.quantity) || 0));
  const some = lines.some(l => (Number(l.deliveredQty) || 0) > 0);
  return all ? 'Completed' : some ? 'Partially Delivered' : 'Approved';
};

const listSOChildren = async (companyId, soId) => {
  const snap = await getDocs(query(collection(db, 'companies', companyId, 'transactions'), where('parentId', '==', soId)));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Per SO line (keyed by its line number): ordered, delivered by OTHER documents,
// and a label. Always counted from the SO's linked documents (one query), so a
// list of orders loaded earlier on this screen can never make it stale.
export const loadSOProgress = async (companyId, so, self) => {
  const soLines = so.lines || [];
  const delivered = soDeliveredFromChildren(soLines, await listSOChildren(companyId, so.id), self ? self.id : null);
  const progress = {};
  soLines.forEach((l, i) => {
    progress[soLineNo(l, i)] = { ordered: Number(l.quantity) || 0, delivered: delivered[i], label: l.description || l.itemName || `Line ${i + 1}` };
  });
  return progress;
};

// Message when a line asks for more than is left on the SO ('' when fine).
export const soOverDeliveryMessage = (lines, progress, soNumber) => {
  if (!progress) return '';
  const want = {};
  (lines || []).forEach(l => {
    const n = Number(l.soLineNumber) || 0;
    if (n > 0 && progress[n]) want[n] = r6((want[n] || 0) + (Number(l.quantity) || 0));
  });
  const over = Object.keys(want).filter(n => want[n] > r6(progress[n].ordered - progress[n].delivered) + 1e-6);
  if (over.length === 0) return '';
  return `Quantity is more than what is left on Sale Order ${soNumber || ''}:\n\n` + over.map(n => {
    const p = progress[n];
    return `• ${p.label}: ordered ${p.ordered}, already delivered ${p.delivered}, remaining ${Math.max(0, r6(p.ordered - p.delivered))} — you entered ${want[n]}`;
  }).join('\n') + '\n\nPlease reduce the quantity to the remaining amount.';
};

// Recompute the SO's per-line delivered / pending quantities from all its linked
// documents and set a valid SO status. Writes only lines / status / updatedAt /
// updatedBy (the SO progress fields a Delivery Challan user may change).
export const recalcSOProgress = async (companyId, saleOrderId, userId) => {
  const soRef = doc(db, 'companies', companyId, 'transactions', saleOrderId);
  const soSnap = await getDoc(soRef);
  if (!soSnap.exists()) return null;
  const so = soSnap.data();
  const soLines = Array.isArray(so.lines) ? so.lines : [];
  const delivered = soDeliveredFromChildren(soLines, await listSOChildren(companyId, saleOrderId), null);
  const updatedLines = soLines.map((l, i) => ({
    ...l, deliveredQty: delivered[i], pendingQty: Math.max(0, r6((Number(l.quantity) || 0) - delivered[i]))
  }));
  const upd = { lines: updatedLines, updatedAt: new Date().toISOString(), ...(userId ? { updatedBy: userId } : {}) };
  const newStatus = soStatusFromLines(updatedLines, so.status);
  if (newStatus !== so.status) upd.status = newStatus;
  await updateDoc(soRef, upd);
  return { ...so, ...upd };
};

// LinkedBadge component for document linking display
// Shows the linked documents' NUMBERS (not a count). `known` = numbers the
// document already carries (no lookup); the rest are looked up once per device.
const LinkedBadge = ({ label, ids, known, max = 3, icon: Icon = FiLink }) => {
  const list = (ids || []).filter(Boolean);
  if (list.length === 0) return null;
  return (
    <LinkedBadgeContainer>
      <Icon size={12} />
      <span>{label}:</span>
      <LinkedDocNumbers ids={list} known={known} max={max} showIcon={false} color="inherit" style={{ fontSize: 'inherit' }} />
    </LinkedBadgeContainer>
  );
};

const DeliveryChallan = () => {
  const location = useLocation();
  const { activeCompany, activeFinancialYear, currentUser, activeBranch, isModuleEnabled } = useAuth();
  // Speed: prepare the first save in the background as soon as this screen opens.
  useEffect(() => {
    if (!activeCompany?.id || !activeFinancialYear?.id) return;
    prewarmSave(activeCompany.id, {
      tranTypeCode: TRAN_TYPE_CODE,
      branchId: activeBranch?.id && activeBranch.id !== 'all' ? activeBranch.id : null,
      financialYearId: activeFinancialYear.id,
    });
  }, [activeCompany?.id, activeFinancialYear?.id, activeBranch?.id]);
  const { canEdit, canDelete, canCancel, canChange } = usePagePermissions('transactions', 'deliveryChallan');
  const showBranchCol = activeBranch && activeBranch.id === 'all';

  const [printConfig, setPrintConfig] = useState(null);
  useEffect(() => { if (activeCompany?.id) loadDocumentFormats(activeCompany.id).then(setPrintConfig); }, [activeCompany]);


  const [viewMode, setViewMode] = useState('list');
  const sort = useTableSort('docDate', 'desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // Paginated list (2026-07-16, Firebase cost): 20 docs/page from Firestore.
  // Any search / status filter / custom column sort switches to a one-time
  // full active-year load so filtering behaves exactly as before.
  const listFiltering = searchQuery.trim() !== '' || statusFilter !== 'All'
    || sort.sortField !== 'docDate' || sort.sortDir !== 'desc';
  const {
    docs: challans,
    loading,
    refresh: loadChallans,
    ensureDoc,
    applyLocal,
    summary: listSummary,
    pagination
  } = useDocListPagination({
    tranTypeCode: TRAN_TYPE_CODE,
    fullMode: listFiltering,
    summarySpec: SUMMARY_SPEC
  });

  const [editingChallan, setEditingChallan] = useState(null);
  const [viewingChallan, setViewingChallan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    docNumber: '', docDate: new Date().toISOString().split('T')[0],
    partyId: '', partyName: '', parentId: '', parentDocNumber: '',
    customerPO: '', // customer's own PO number — from the Sale Order, carried on to the invoice
    vehicleNo: '', driverName: '', driverPhone: '',
    deliveryAddress: '', notes: '', status: 'Draft',
    employeeId: '', employeeName: '', jobId: '', jobName: '',
    fromLocationId: '', fromLocationName: ''
  });
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE }]);

  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [customerPricing, setCustomerPricing] = useState(EMPTY_PRICING);
  const [approvedSaleOrders, setApprovedSaleOrders] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [availableBatches, setAvailableBatches] = useState({});  // keyed by itemId
  const [availableSerials, setAvailableSerials] = useState({});  // keyed by itemId
  const [stockBalances, setStockBalances] = useState({});
  // FLW-03: per SO line { ordered, delivered (by other documents), label } for the
  // Sale Order this challan is made from (null when not linked / not loaded).
  const [soProgress, setSoProgress] = useState(null);
  const soPickRef = useRef('');

  // Challan list now comes from useDocListPagination above (loadChallans = its
  // refresh). The old full-history getTransactions load lived here.

  // Tier 1 (2026-07-27, Firebase cost): customers/items come from the device-local
  // master cache (getMaster* → IndexedDB, 0 reads per open with a throttled
  // background change-check) instead of a full getDocs on every open. Each screen
  // keeps its own client-side filter/sort on the returned rows.
  const loadCustomers = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getCustomersCached(activeCompany.id);
      setCustomers([...rows].sort((a, b) => (a.companyName || '').localeCompare(b.companyName || '')));
    } catch (error) { console.error('Error loading customers:', error); }
  }, [activeCompany]);

  const loadItems = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getItemsCached(activeCompany.id);
      setItems(rows.filter(i => i.isDeleted !== true).sort((a, b) => (a.itemName || '').localeCompare(b.itemName || '')));
    } catch (error) { console.error('Error loading items:', error); }
  }, [activeCompany]);

  // ── D3 (2026-07-23, Firebase cost): the loader below feeds ONLY the form's
  // "Link to Sale Order" dropdown — nothing on the list view uses it. It used
  // to run on every page open AND after every save, scanning full sale-order
  // history (all Approved + all Partially Delivered SOs ever). It now loads
  // ONCE, lazily, the first time a form is actually opened. List browsing and
  // saving cost nothing.
  const formDataLoadedRef = useRef(false);

  const loadApprovedSaleOrders = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      // Load approved SOs from unified transactions collection
      const approvedSOs = await getTransactions(activeCompany.id, {
        tranTypeCode: 'SO', status: 'Approved'
      });
      const partialSOs = await getTransactions(activeCompany.id, {
        tranTypeCode: 'SO', status: 'Partially Delivered'
      });
      setApprovedSaleOrders([...approvedSOs, ...partialSOs]);
    } catch (error) { console.error('Error loading sale orders:', error); }
  }, [activeCompany]);

  // Loads the form-only dropdown data once per page session (see D3 note above).
  const ensureFormData = useCallback(async () => {
    if (formDataLoadedRef.current || !activeCompany?.id) return;
    formDataLoadedRef.current = true;
    try {
      await Promise.all([loadApprovedSaleOrders()]);
    } catch (error) {
      formDataLoadedRef.current = false; // let a later form open retry
      console.error('Error loading form link data:', error);
    }
  }, [activeCompany, loadApprovedSaleOrders]);

  const loadEmployees = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getEmployeesCached(activeCompany.id);
      setEmployees(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading employees:', error); }
  }, [activeCompany]);

  const loadJobs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getJobsCached(activeCompany.id);
      setJobs(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading jobs:', error); }
  }, [activeCompany]);

  const loadLocations = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getLocationsCached(activeCompany.id);
      setLocations(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading locations:', error); }
  }, [activeCompany]);

  // Firebase cost: stock is fetched ONE ITEM AT A TIME, on demand. The old
  // mount-time load scanned the company's ENTIRE stockFlow history just to draw
  // badges the user may never look at. Now the dropdown shows a "stock?" chip
  // and this runs only when the user taps it (or when the availability check
  // needs an item it doesn't already have).
  const requestItemStock = useCallback(async (itemId) => {
    if (!activeCompany?.id || !itemId) return;
    try {
      const qty = await getItemStockCached(activeCompany.id, itemId, {
        branchId: (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : undefined
      });
      setStockBalances(prev => ({ ...prev, [itemId]: qty }));
    } catch (e) { console.error('Error loading item stock:', e); }
  }, [activeCompany, activeBranch]);

  // NOTE: the challan list itself loads inside useDocListPagination — not here.
  // D3: the sale-order loader is NOT here anymore — it loads lazily on first
  // form open (ensureFormData), since only the form uses it.
  useEffect(() => {
    loadCustomers(); loadItems();
    loadEmployees(); loadJobs(); loadLocations();
  }, [loadCustomers, loadItems, loadEmployees, loadJobs, loadLocations]);

  // Handle navigation from reports (e.g., Journal Report clicking on document #).
  // With pagination the target may not be in the loaded page — ensureDoc
  // fetches that single document by id when needed.
  useEffect(() => {
    const targetId = location.state?.viewTransactionId;
    if (!targetId) return;
    ensureDoc(targetId).then(transaction => {
      if (transaction) {
        setViewingChallan(transaction);
        setViewMode('view');
        // Clear the state to prevent re-triggering
        window.history.replaceState({}, document.title);
      }
    });
  }, [location.state, ensureDoc]);

  const filteredChallans = useMemo(() => {
    let filtered = challans;
    if (statusFilter !== 'All') filtered = filtered.filter(o => o.status === statusFilter);
    if (searchQuery.trim()) {
      const sq = searchQuery.toLowerCase();
      filtered = filtered.filter(o =>
        o.docNumber?.toLowerCase().includes(sq) ||
        o.partyName?.toLowerCase().includes(sq) ||
        o.parentDocNumber?.toLowerCase().includes(sq) ||
        o.customerPO?.toLowerCase().includes(sq) ||
        o.vehicleNo?.toLowerCase().includes(sq)
      );
    }
    return sort.sortItems(filtered);
  }, [challans, statusFilter, searchQuery, sort]);

  const calculateLine = (line) => {
    const total = (Number(line.quantity) || 0) * (Number(line.unitPrice) || 0);
    return { ...line, lineTotal: Math.round(total * 100) / 100 };
  };

  const totals = useMemo(() => {
    const lines = lineItems.map(calculateLine);
    const subtotal = lines.reduce((s, l) => s + l.lineTotal, 0);
    return { subtotal: Math.round(subtotal * 100) / 100, totalAmount: Math.round(subtotal * 100) / 100 };
  }, [lineItems]);

  const handleHeaderChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    // Auto-fill the salesperson (employee) from the customer's saved default.
    const spPatch = customer?.salesPersonId
      ? { employeeId: customer.salesPersonId, employeeName: customer.salesPersonName || '' }
      : {};
    setFormData(prev => ({ ...prev, partyId: customerId, partyName: customer?.companyName || '', parentId: '', parentDocNumber: '', ...spPatch }));
    setLineItems([{ ...EMPTY_LINE }]);
    setSoProgress(null);
    soPickRef.current = '';
    if (customerId) {
      loadCustomerPricing(activeCompany?.id, customerId, isModuleEnabled)
        .then(setCustomerPricing)
        .catch(() => setCustomerPricing(EMPTY_PRICING));
    } else {
      setCustomerPricing(EMPTY_PRICING);
    }
  };

  const handleSaleOrderChange = async (parentId) => {
    const so = approvedSaleOrders.find(o => o.id === parentId);
    soPickRef.current = so ? parentId : '';
    if (!so) {
      setFormData(prev => ({ ...prev, parentId: '', parentDocNumber: '' }));
      setSoProgress(null);
      return;
    }
    setFormData(prev => ({
      ...prev, parentId, parentDocNumber: so.docNumber,
      partyId: so.partyId, partyName: so.partyName,
      deliveryAddress: so.deliveryAddress || '',
      ...(customerPOOf(so) ? { customerPO: customerPOOf(so) } : {}),
      ...carryEmployeeJob(so)
    }));
    // FLW-03: what each SO line already has delivered (by other documents).
    let progress = null;
    try {
      progress = await loadSOProgress(activeCompany.id, so,
        editingChallan && editingChallan.parentId === so.id ? editingChallan : null);
    } catch (e) { console.error('Error loading sale order progress:', e); }
    if (soPickRef.current !== parentId) return; // another order was picked meanwhile
    setSoProgress(progress);
    const remainingOf = (n) => (progress && progress[n]) ? Math.max(0, r6(progress[n].ordered - progress[n].delivered)) : null;
    // Fully delivered lines are not offered; the rest pre-fill with what is left.
    const openLines = (so.lines || []).map((l, idx) => ({ l, n: soLineNo(l, idx) }))
      .filter(({ n }) => remainingOf(n) === null || remainingOf(n) > 0);
    if ((so.lines || []).length > 0 && openLines.length === 0) {
      alert(`Every line of Sale Order ${so.docNumber || ''} is already fully delivered.`);
      soPickRef.current = '';
      setFormData(prev => ({ ...prev, parentId: '', parentDocNumber: '' }));
      setSoProgress(null);
      setLineItems([{ ...EMPTY_LINE }]);
      return;
    }
    if (openLines.length > 0) {
      setLineItems(openLines.map(({ l, n }) => {
        const item = items.find(i => i.id === l.itemId);
        const trackingType = l.trackingType || item?.trackingType || 'None';
        // Load available batches/serials for tracked items
        if (l.itemId && trackingType === 'Batch') {
          getAvailableBatches(activeCompany.id, l.itemId).then(batches => {
            setAvailableBatches(prev => ({ ...prev, [l.itemId]: batches }));
          });
        } else if (l.itemId && trackingType === 'Serial') {
          getAvailableSerials(activeCompany.id, l.itemId).then(serials => {
            setAvailableSerials(prev => ({ ...prev, [l.itemId]: serials }));
          });
        }
        const remaining = remainingOf(n);
        return {
          ...EMPTY_LINE, itemId: l.itemId || '', description: l.description || '',
          ...hsCodePatch(l, item),
          soLineNumber: n,
          orderedQty: l.quantity || 0, quantity: remaining === null ? (l.quantity || 0) : remaining,
          unitOfMeasure: l.unitOfMeasure || '',
          unitPrice: l.unitPrice || 0,
          unitCost: Number(item?.averageCost) || l.unitCost || item?.purchasePrice || 0, // Store cost for COGS — INV-04: weighted average first
          // Carry the SO line's discount % and tax so they flow through to the invoice.
          discountPercent: l.discountPercent || 0,
          discountAmount: l.discountAmount || 0,
          taxable: l.taxable || false,
          taxRate: l.taxRate ?? 0,
          trackingType,
          batchNumber: l.batchNumber || '',
          serialNumbers: l.serialNumbers || []
        };
      }));
    }
  };

  const handleLineChange = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // Size-mode line edits: recompute Total SQft when L / H / Thickness / Pieces / sizeUnit change.
      if (updated[index].hasSize && ['length', 'height', 'thickness', 'pieces', 'sizeUnit'].includes(field)) {
        updated[index] = recomputeSizeLine(updated[index]);
      }

      if (field === 'unitCode' && updated[index].itemId) {
        const item = items.find(i => i.id === updated[index].itemId);
        if (item) {
          const patch = applyUnitChangeToLine(item, value, { priceField: 'salePrice', costFromAverage: true });
          updated[index] = { ...updated[index], ...patch };
          const ovr = getOverridePrice(customerPricing, updated[index].itemId);
          if (ovr !== null) updated[index].unitPrice = ovr;
          // Show Two Units: switching "Rate per" between the item's two units keeps the same physical qty
          // (not on lines linked to another document — their qty is tracked against it).
          const twoQ = (formData.parentId || prev[index].sourceDocId || prev[index].poLineId) ? null
            : twoUnitSwitchQty(item, prev[index].unitCode || prev[index].unitOfMeasure, value, prev[index].quantity);
          if (twoQ !== null) updated[index].quantity = twoQ;
        }
      }

      if (field === 'itemId' && value) {
        const item = items.find(i => i.id === value);
        if (item) {
          updated[index].description = item.itemName || '';
          // HS Code from the item (carried on to the Sale Invoice).
          updated[index].hsCode = item.hsCode || '';
          const seeded = seedLineUnitFromItem(item, { priceField: 'salePrice', defaultUnitField: 'defaultSaleUnit', costFromAverage: true });
          updated[index] = { ...updated[index], ...seeded };
          updated[index].trackingType = item.trackingType || 'None';
          updated[index].batchNumber = '';
          updated[index].serialNumbers = [];
          const ovr = getOverridePrice(customerPricing, value);
          if (ovr !== null) updated[index].unitPrice = ovr;

          // Size mode: prime with item defaults and force unit to SQft.
          if (item.hasSize) {
            updated[index].hasSize = true;
            updated[index].sizeUnit = item.sizeUnit === 'cm' ? 'cm' : 'inches';
            updated[index].length = Number(item.defaultLength) || 0;
            updated[index].height = Number(item.defaultHeight) || 0;
            updated[index].thickness = Number(item.defaultThickness) || 0;
            updated[index].pieces = updated[index].pieces || 1;
            updated[index].unitOfMeasure = 'SQft';
            updated[index].unitCode = 'SQft';
            updated[index] = recomputeSizeLine(updated[index]);
          } else {
            updated[index].hasSize = false;
          }
          // Load available batches/serials for this item
          if (item.trackingType === 'Batch') {
            getAvailableBatches(activeCompany.id, value).then(batches => {
              setAvailableBatches(prev => ({ ...prev, [value]: batches }));
              if (batches.length > 0) {
                setLineItems(prev => {
                  const u = [...prev];
                  if (u[index] && u[index].itemId === value) {
                    u[index] = { ...u[index], batchNumber: batches[0].batchNumber };
                  }
                  return u;
                });
              }
            });
          } else if (item.trackingType === 'Serial') {
            getAvailableSerials(activeCompany.id, value).then(serials => {
              setAvailableSerials(prev => ({ ...prev, [value]: serials }));
            });
          }
        }
      }
      updated[index] = calculateLine(updated[index]);
      return updated;
    });
  };

  const addLine = () => setLineItems(prev => [...prev, { ...EMPTY_LINE }]);

  const lineItemRefs = useRef([]);
  const pendingFocusRowRef = useRef(null);
  useEffect(() => {
    if (pendingFocusRowRef.current == null) return;
    const rowIdx = pendingFocusRowRef.current;
    pendingFocusRowRef.current = null;
    setTimeout(() => {
      const el = lineItemRefs.current[rowIdx];
      if (el && typeof el.focus === 'function') el.focus();
    }, 50);
  }, [lineItems.length]);

  const handleLastFieldEnter = (e, index) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (formData.parentId) return;
    const isLastRow = index === lineItems.length - 1;
    const line = lineItems[index];
    const hasData = !!line.itemId || (Number(line.quantity) > 0);
    if (!hasData) return;
    if (isLastRow) {
      // eslint-disable-next-line no-alert
      if (window.confirm('Add a new line item?')) {
        pendingFocusRowRef.current = lineItems.length;
        addLine();
      }
    } else {
      const next = lineItemRefs.current[index + 1];
      if (next && typeof next.focus === 'function') next.focus();
    }
  };
  const removeLine = (index) => { if (lineItems.length <= 1) return; setLineItems(prev => prev.filter((_, i) => i !== index)); };

  const resetForm = () => {
    setFormData({ docNumber: '', docDate: new Date().toISOString().split('T')[0], partyId: '', partyName: '', parentId: '', parentDocNumber: '', customerPO: '', vehicleNo: '', driverName: '', driverPhone: '', deliveryAddress: '', notes: '', status: 'Draft', employeeId: '', employeeName: '', jobId: '', jobName: '', fromLocationId: '', fromLocationName: '' });
    setLineItems([{ ...EMPTY_LINE }]);
    setEditingChallan(null);
    setSoProgress(null);
    soPickRef.current = '';
  };

  const handleCreate = () => { resetForm(); ensureFormData(); setViewMode('form'); };
  const handleEdit = (dc) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    ensureFormData(); // D3: load sale-order link data on demand
    setEditingChallan(dc);
    setFormData({ docNumber: dc.docNumber || '', docDate: dc.docDate || new Date().toISOString().split('T')[0], partyId: dc.partyId || '', partyName: dc.partyName || '', parentId: dc.parentId || '', parentDocNumber: dc.parentDocNumber || '', customerPO: dc.customerPO || '', vehicleNo: dc.vehicleNo || '', driverName: dc.driverName || '', driverPhone: dc.driverPhone || '', deliveryAddress: dc.deliveryAddress || '', notes: dc.notes || '', status: dc.status || 'Draft', employeeId: dc.employeeId || '', employeeName: dc.employeeName || '', jobId: dc.jobId || '', jobName: dc.jobName || '', fromLocationId: dc.fromLocationId || '', fromLocationName: dc.fromLocationName || '' });
    setLineItems(dc.lines?.length > 0 ? dc.lines.map(l => ({ ...EMPTY_LINE, ...l })) : [{ ...EMPTY_LINE }]);
    // FLW-03: show / check what is left on the linked Sale Order (1 read).
    setSoProgress(null);
    soPickRef.current = dc.parentId || '';
    if (dc.parentId && activeCompany?.id) {
      (async () => {
        try {
          const snap = await getDoc(doc(db, 'companies', activeCompany.id, 'transactions', dc.parentId));
          if (!snap.exists() || snap.data().tranTypeCode !== 'SO') return;
          const so = { id: snap.id, ...snap.data() };
          const progress = await loadSOProgress(activeCompany.id, so, dc);
          if (soPickRef.current !== dc.parentId) return;
          setSoProgress(progress);
          // Older challans: tag each line with the SO line it belongs to.
          const tags = tagSOLineNumbers(so.lines || [], dc.lines || [], progress);
          setLineItems(prev => prev.map((l, i) => (Number(l.soLineNumber) > 0 || !tags[i]) ? l : { ...l, soLineNumber: tags[i] }));
        } catch (e) { console.error('Error loading sale order progress:', e); }
      })();
    }
    if (dc.partyId) {
      loadCustomerPricing(activeCompany?.id, dc.partyId, isModuleEnabled)
        .then(setCustomerPricing)
        .catch(() => setCustomerPricing(EMPTY_PRICING));
    } else {
      setCustomerPricing(EMPTY_PRICING);
    }
    setViewMode('form');
  };
  const handleView = (dc) => { setViewingChallan(dc); setViewMode('view'); };

  // Recompute the parent Sale Order's per-line delivered/pending quantities from
  // all its linked documents (active challans + posted direct invoices), matched
  // by SO line, and set a valid SO status (Partially Delivered / Completed).
  const recalcSaleOrderProgress = async (saleOrderId) => {
    await recalcSOProgress(activeCompany.id, saleOrderId, currentUser.uid);
  };

  const handleSave = async (saveStatus) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (missingLocation(activeCompany, formData.fromLocationId)) { alert(LOCATION_REQUIRED_MSG); return; }
    if (!formData.partyId) { alert('Please select a customer'); return; }
    const validLines = lineItems.filter(l => l.description && l.quantity > 0);
    if (validLines.length === 0) { alert('Please add at least one valid line item with delivered quantity'); return; }

    // Validate financial year
    if (!activeFinancialYear?.id) {
      alert('No active financial year found. Please set an active financial year in Settings > Financial Year.');
      return;
    }

    // Validate batch/serial tracking
    for (const line of validLines) {
      if (line.trackingType === 'Batch' && !line.batchNumber?.trim()) {
        alert(`Batch selection is required for "${line.itemName || line.description}"`); return;
      }
      if (line.trackingType === 'Serial') {
        const serials = line.serialNumbers || [];
        const qty = line.quantity || 0;
        if (serials.length !== qty) {
          alert(`"${line.itemName || line.description}" requires exactly ${qty} serial number(s). You selected ${serials.length}.`); return;
        }
      }
    }

    // FLW-03: never deliver more than what is left on the Sale Order.
    if (formData.parentId && soProgress) {
      const overMsg = soOverDeliveryMessage(validLines, soProgress, formData.parentDocNumber);
      if (overMsg) { alert(overMsg); return; }
    }

    // Future date warning
    if (isFutureDate(formData.docDate)) {
      if (!window.confirm('Document date is in the future. Continue?')) return;
    }

    // Stock availability check when dispatching
    if (saveStatus === 'Dispatched') {
      const stockItems = validLines.filter(l => l.itemId).map(l => ({ itemId: l.itemId, itemName: l.itemName || l.description, quantity: l.quantity, unitCode: l.unitCode || l.unitOfMeasure }));
      if (stockItems.length > 0) {
        // Firebase cost: reuse the on-screen stock badges + cached items.
        const itemsById = {};
        items.forEach(i => { itemsById[i.id] = i; });
        const insufficient = await checkStockAvailability(activeCompany.id, stockItems, { stockMap: stockBalances, itemsMap: itemsById, branchId: (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : null });
        if (insufficient.length > 0) {
          const msg = insufficient.map(i => `${i.itemName}: available ${i.available} ${i.unit || ''}, requested ${i.requested} ${i.unit || ''}`).join('\n');
          if (!window.confirm(`Insufficient stock:\n${msg}\n\nContinue anyway?`)) return;
        }
      }
    }

    setSaving(true);
    try {
      const calculatedLines = validLines.map((l, i) => ({ ...calculateLine(l), lineNumber: i + 1 }));
      const status = saveStatus || formData.status || 'Draft';
      const dcData = {
        tranTypeCode: TRAN_TYPE_CODE,
        docDate: formData.docDate,
        partyId: formData.partyId,
        partyName: formData.partyName,
        parentId: formData.parentId || null,
        parentDocNumber: formData.parentDocNumber || '',
        customerPO: formData.customerPO || '',
        vehicleNo: formData.vehicleNo,
        driverName: formData.driverName,
        driverPhone: formData.driverPhone,
        deliveryAddress: formData.deliveryAddress,
        employeeId: formData.employeeId,
        employeeName: formData.employeeName,
        jobId: formData.jobId,
        jobName: formData.jobName,
        fromLocationId: formData.fromLocationId,
        fromLocationName: formData.fromLocationName,
        branchId: activeBranch?.id || null,
        branchName: activeBranch?.branchName || null,
        subtotal: totals.subtotal,
        totalAmount: totals.totalAmount,
        notes: formData.notes,
        status,
        lines: calculatedLines,
        financialYearId: activeFinancialYear.id
      };

      if (status === 'Dispatched' && formData.status !== 'Dispatched') {
        dcData.dispatchedBy = currentUser.uid;
        dcData.dispatchedAt = new Date().toISOString();
      }

      let savedDocId;
      let savedDocNumber;
      let newTranData = null;
      if (editingChallan) {
        await updateTransaction(activeCompany.id, editingChallan.id, dcData, currentUser.uid);
        savedDocId = editingChallan.id;
        savedDocNumber = editingChallan.docNumber;
      } else {
        newTranData = await createTransaction(activeCompany.id, dcData, currentUser.uid);
        savedDocId = newTranData.id;
        savedDocNumber = newTranData.docNumber;
      }

      // Firebase cost: keep the in-memory stock badges correct after a dispatch
      // that NEWLY reduced stock, so the next DC this session checks fresh numbers.
      if (status === 'Dispatched' && formData.status !== 'Dispatched') {
        setStockBalances(prev => {
          const next = { ...prev };
          calculatedLines.forEach(l => {
            if (!l.itemId) return;
            const it = items.find(i => i.id === l.itemId);
            if (it?.itemType === 'service') return;
            // On-demand stock: only adjust items whose balance we actually know.
            // Decrementing an unknown item would invent a bogus (negative) figure
            // that the next availability check would trust — leave it unknown and
            // let it be fetched fresh when needed.
            if (next[l.itemId] === undefined) return;
            const { requestedBase } = toBaseQty(it || {}, l.quantity, l.unitCode || l.unitOfMeasure);
            next[l.itemId] = Number(next[l.itemId]) - requestedBase;
          });
          return next;
        });
      }

      // Stock flow is already generated by createTransaction/updateTransaction above
      // No need to call generateStockFlow() again here

      // Update parent Sale Order: recalc per-line delivered/pending from its linked
      // documents, then set SO status (Partially Delivered / Completed). Runs when
      // this challan counts now or counted before (e.g. re-saved as Draft).
      const soToRecalc = new Set();
      if (formData.parentId && DC_ACTIVE(status)) soToRecalc.add(formData.parentId);
      if (editingChallan && editingChallan.parentId && DC_ACTIVE(editingChallan.status)) soToRecalc.add(editingChallan.parentId);
      if (soToRecalc.size > 0) {
        for (const soId of soToRecalc) {
          try {
            await recalcSaleOrderProgress(soId);
          } catch (err) { console.error('Error updating sale order progress:', err); }
        }
        // D3: the parent SO's status/progress just changed — mark the form link
        // data stale so the NEXT form open refetches it, instead of paying for
        // it here on the list view.
        formDataLoadedRef.current = false;
      }

      // Optimistic UI update — show the saved record immediately so the Save
      // button releases without waiting on a full collection re-fetch.
      const savedRecord = editingChallan
        ? { ...editingChallan, ...dcData, id: savedDocId, status }
        : { ...newTranData, status };
      applyLocal(prev => editingChallan
        ? prev.map(r => r.id === savedDocId ? savedRecord : r)
        : [savedRecord, ...prev]);

      // Stock moved — drop the per-item cached quantities so the next "stock?"
      // lookup (here or on any other form this session) reads fresh numbers.
      invalidateItemStock(activeCompany.id);

      setViewMode('list');
      resetForm();

      // D3: only the (paginated) challan list is refreshed from the server — the
      // sale-order link data is form-only and reloads on the next form open.
      // Background refresh — reconciles with server data without blocking UI.
      loadChallans().catch(err =>
        console.error('Background refresh failed:', err)
      );
    } catch (error) { console.error('Error saving delivery challan:', error); alert('Error saving delivery challan: ' + error.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (dc) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (!window.confirm(`Delete ${dc.docNumber}?`)) return;
    try {
      // ACC-04 fix (2026-09-30): deleteTransaction() runs the safety checks FIRST
      // (linked documents, receipts, FBR) and only then removes ledger + stock rows.
      // Wiping them here beforehand left a blocked document with no accounting.
      await deleteTransaction(activeCompany.id, dc.id);
      if (dc.parentId) {
        try { await recalcSaleOrderProgress(dc.parentId); } catch (e) { console.error('SO recalc after DC delete:', e); }
        // D3: SO progress changed — mark the form link data stale so the NEXT
        // form open refetches it, instead of paying for it here on the list view.
        formDataLoadedRef.current = false;
      }
      await loadChallans();
    }
    catch (error) { console.error('Error deleting challan:', error); alert('Could not delete: ' + (error?.message || 'unknown error')); }
  };

  const handleCancel = async (dc) => {
    if (!canCancel) { alert('You do not have permission to cancel.'); return; }
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (dc.status === 'Invoiced') { alert('Invoiced challans cannot be cancelled'); return; }
    if (!window.confirm(`Cancel ${dc.docNumber}?`)) return;
    try {
      // Delete stock flow if reverting from Dispatched/Delivered
      if (dc.status === 'Dispatched' || dc.status === 'Delivered') {
        await deleteStockFlow(activeCompany.id, dc.id);
      }
      await updateTransactionStatus(activeCompany.id, dc.id, 'Draft', currentUser.uid);
      // AUDIT (2026-11-04): record the cancel (challan goes back to Draft).
      writeAuditLog(activeCompany.id, currentUser?.uid || null, 'cancel', 'DC', dc.id, {
        docNumber: dc.docNumber || '', docDate: dc.docDate || '', partyName: dc.partyName || '',
        amount: Number(dc.totalAmount) || 0, branchId: dc.branchId || null, branchName: dc.branchName || '',
        status: dc.status || '', note: 'Reverted to Draft',
      }).catch(() => { /* never block on logging */ });
      if (dc.parentId) {
        try { await recalcSaleOrderProgress(dc.parentId); } catch (e) { console.error('SO recalc after DC cancel:', e); }
        formDataLoadedRef.current = false; // D3: refresh link data on next form open
      }
      await loadChallans();
    } catch (error) { console.error('Error cancelling challan:', error); }
  };

  const handlePrint = (dc) => {
    const target = dc || viewingChallan;
    const cust = customers.find(c => c.id === target?.partyId);
    const enrichedLines = (target?.lines || []).map(l => {
      const it = items.find(i => i.id === l.itemId);
      return { ...l, imageUrl: it?.imageUrl || l.imageUrl || '' };
    });
    const enriched = { ...target, lines: enrichedLines, partyNTN: cust?.ntn || target?.partyNTN || '', partySTRN: cust?.strn || target?.partySTRN || '' };
    printDocument('deliveryChallan', enriched, activeCompany, printConfig, activeBranch);
  };

  // Keyboard shortcuts
  useDocumentShortcuts({
    onNew: canEdit ? handleCreate : undefined,
    onSave: canEdit ? () => handleSave('Dispatched') : undefined,
    viewMode,
    saving
  });

  const renderListView = () => (
    <>
      <ActionBar>
        <SearchBox><FiSearch /><input type="text" placeholder="Search by DC#, customer, order#, vehicle..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} /></SearchBox>
        <FilterGroup>
          <StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="All">All Status</option><option value="Draft">Draft</option>
            <option value="Dispatched">Dispatched</option><option value="Delivered">Delivered</option>
            <option value="Invoiced">Invoiced</option>
          </StatusFilterSelect>
          {canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> New Challan</TranAddButton>}
        </FilterGroup>
      </ActionBar>

      {loading ? <LoadingMessage>Loading delivery challans...</LoadingMessage> : filteredChallans.length === 0 ? (
        <TranEmptyState><FiFileText size={48} /><h3>No Delivery Challans</h3><p>{challans.length === 0 ? 'Create your first delivery challan' : 'No challans match your filters'}</p>
          {challans.length === 0 && canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> Create Challan</TranAddButton>}</TranEmptyState>
      ) : (
        <TableContainer><SortContext.Provider value={sort.ctxValue}><Table><thead><tr>
          <SortableTh field="docNumber">DC #</SortableTh><SortableTh field="docDate">Date</SortableTh>{showBranchCol && <SortableTh field="branchName">Branch</SortableTh>}<SortableTh field="partyName">Customer</SortableTh><SortableTh field="parentDocNumber">Sale Order</SortableTh><SortableTh field="customerPO">Customer PO</SortableTh><SortableTh field="vehicleNo">Vehicle</SortableTh>
          <SortableTh field="status" style={{ textAlign: 'center' }}>Status</SortableTh><Th style={{ textAlign: 'center' }}>Links</Th><Th style={{ textAlign: 'center' }}>Actions</Th>
        </tr></thead><tbody>
          {filteredChallans.map(dc => (
            <Tr key={dc.id}>
              <Td><strong>{dc.docNumber}</strong></Td><Td>{dc.docDate}</Td>{showBranchCol && <Td>{dc.branchName || '-'}</Td>}<Td>{dc.partyName}</Td>
              <Td>{dc.parentDocNumber || '-'}</Td><Td>{dc.customerPO || '-'}</Td><Td>{dc.vehicleNo || '-'}</Td>
              <Td style={{ textAlign: 'center' }}><StatusBadge $color={STATUS_COLORS[dc.status]}>{dc.status}</StatusBadge></Td>
              <Td style={{ textAlign: 'center' }}>
                <LinkedBadge label="SO" {...parentLinks(dc, 'SO')} />
                <LinkedBadge label="SI" ids={dc.childIds} />
              </Td>
              <Td style={{ textAlign: 'center' }}><ActionGroup>
                <IconBtn title="View" onClick={() => handleView(dc)}><FiEye /></IconBtn>
                {canChange && dc.status !== 'Invoiced' && <IconBtn title="Edit" onClick={() => handleEdit(dc)}><FiEdit2 /></IconBtn>}
                <IconBtn title="Print" onClick={() => handlePrint(dc)}><FiPrinter /></IconBtn>
                {dc.status !== 'Draft' && dc.status !== 'Cancelled' && (
                  <SendWhatsAppButton templateKey="deliveryChallan" doc={dc} party={customers.find(c => c.id === dc.partyId)} variant="icon" />
                )}
                {canDelete && dc.status !== 'Invoiced' && <IconBtn title="Delete" $danger onClick={() => handleDelete(dc)}><FiTrash2 /></IconBtn>}
                {canCancel && canEdit && dc.status === 'Dispatched' && <IconBtn title="Revert to Draft" onClick={() => handleCancel(dc)}><FiXCircle /></IconBtn>}
              </ActionGroup></Td>
            </Tr>
          ))}
        </tbody></Table></SortContext.Provider></TableContainer>
      )}
      <DocListPagination {...pagination} />

      <SummaryBar><span>Total: <strong>{listFiltering ? filteredChallans.length : pagination.totalCount}</strong> challan(s)</span>
        <span>Draft: <strong>{listSummary.counts.Draft || 0}</strong> | Dispatched: <strong>{listSummary.counts.Dispatched || 0}</strong> | Delivered: <strong>{listSummary.counts.Delivered || 0}</strong> | Invoiced: <strong>{listSummary.counts.Invoiced || 0}</strong></span>
      </SummaryBar>
    </>
  );

  const renderViewMode = () => {
    if (!viewingChallan) return null; const dc = viewingChallan;
    return (<>
      <FormHeader>
        <BackButton onClick={() => { setViewMode('list'); setViewingChallan(null); }}><FiArrowLeft /> Back to List</BackButton>
        <FormTitle>{dc.docNumber}<StatusBadge $color={STATUS_COLORS[dc.status]} style={{ marginLeft: 12 }}>{dc.status}</StatusBadge></FormTitle>
        <FormActions>
          {canChange && dc.status !== 'Invoiced' && <SaveButton onClick={() => handleEdit(dc)}><FiEdit2 /> Edit</SaveButton>}
          <SaveButton $secondary onClick={() => handlePrint(dc)}><FiPrinter /> Print</SaveButton>
        </FormActions>
      </FormHeader>
      <FormCard><SectionTitle>Challan Details</SectionTitle>
        <InfoGrid>
          <InfoItem><label>DC Number</label><span>{dc.docNumber}</span></InfoItem>
          <InfoItem><label>Date</label><span>{dc.docDate}</span></InfoItem>
          <InfoItem><label>Customer</label><span>{dc.partyName}</span></InfoItem>
          <InfoItem><label>Sale Order Ref</label><span>{dc.parentDocNumber || '-'}</span></InfoItem>
          <InfoItem><label>Customer PO #</label><span>{dc.customerPO || '-'}</span></InfoItem>
          <InfoItem><label>Vehicle No</label><span>{dc.vehicleNo || '-'}</span></InfoItem>
          <InfoItem><label>Driver Name</label><span>{dc.driverName || '-'}</span></InfoItem>
          <InfoItem><label>Driver Phone</label><span>{dc.driverPhone || '-'}</span></InfoItem>
          <InfoItem><label>Delivery Address</label><span>{dc.deliveryAddress || '-'}</span></InfoItem>
          <InfoItem><label>Employee</label><span>{dc.employeeName || '-'}</span></InfoItem>
          <InfoItem><label>Job</label><span>{dc.jobName || '-'}</span></InfoItem>
          <InfoItem><label>From Location</label><span>{dc.fromLocationName || '-'}</span></InfoItem>
        </InfoGrid>
        {(dc.parentId || dc.childIds?.length > 0) && (
          <LinkedDocsSection>
            <LinkedBadge label="Linked SO" {...parentLinks(dc, 'SO')} max={0} />
            <LinkedBadge label="Linked SI" ids={dc.childIds} max={0} />
          </LinkedDocsSection>
        )}
      </FormCard>
      <FormCard><SectionTitle>Line Items</SectionTitle>
        <TableContainer><Table><thead><tr><Th>#</Th><Th>Description</Th><Th style={{ textAlign: 'right' }}>Ordered Qty</Th><Th style={{ textAlign: 'right' }}>Delivered Qty</Th></tr></thead>
          <tbody>{(dc.lines || []).map((line, i) => (
            <Tr key={i}><Td>{i + 1}</Td><Td>{line.description}</Td><Td style={{ textAlign: 'right' }}>{line.orderedQty || '-'}</Td><Td style={{ textAlign: 'right' }}>{line.quantity}{line.unitOfMeasure ? <span style={{ fontSize: '11px', color: '#64748B', marginLeft: '4px' }}>{line.unitOfMeasure}</span> : ''}<TwoUnitNote line={line} /></Td></Tr>
          ))}</tbody></Table></TableContainer>
      </FormCard>
      {dc.notes && <FormCard><SectionTitle>Notes</SectionTitle><NotesText>{dc.notes}</NotesText></FormCard>}
      <AttachmentPanel transactionId={dc?.id} attachment={dc?.attachment} canEdit={false} />
    </>);
  };

  const renderFormView = () => {
    // Only this branch's orders (see utils/branchLink.js).
    const customerSaleOrders = approvedSaleOrders.filter(o => !formData.partyId || o.partyId === formData.partyId)
      .filter(o => inActiveBranch(o, activeBranch, formData.parentId));
    // FLW-03: Already Delivered / Remaining columns for a challan made from a Sale Order.
    const soCols = !!formData.parentId && !!soProgress;
    return (<>
      <FormHeader>
        <BackButton onClick={() => { setViewMode('list'); resetForm(); }}><FiArrowLeft /> Back to List</BackButton>
        <FormTitle>{editingChallan ? `Edit: ${editingChallan.docNumber}` : 'New Delivery Challan'}</FormTitle>
        <FormActions>
          <SaveButton $secondary onClick={() => { setViewMode('list'); resetForm(); }}><FiX /> Cancel</SaveButton>
          {canEdit && <SaveButton onClick={() => handleSave('Draft')} disabled={saving}><FiSave /> {saving ? 'Saving...' : 'Save Draft'}</SaveButton>}
          {canEdit && (formData.status === 'Draft' || !editingChallan) && (
            <SaveButton $approve onClick={() => handleSave('Dispatched')} disabled={saving}><FiTruck /> Dispatch</SaveButton>
          )}
        </FormActions>
      </FormHeader>
      <FormCard><SectionTitle>Challan Details</SectionTitle>
        <FormGrid>
          <FormGroup><Label>DC Number</Label><Input value={editingChallan?.docNumber || 'Auto-generated'} disabled /></FormGroup>
          <FormGroup><Label>DC Date *</Label><Input type="date" value={formData.docDate} onChange={e => handleHeaderChange('docDate', e.target.value)} /></FormGroup>
          <FormGroup><Label>Status</Label><Input value={formData.status} disabled /></FormGroup>
          <FormGroup><Label>Link to Sale Order</Label>
            <Select value={formData.parentId} onChange={e => handleSaleOrderChange(e.target.value)}>
              <option value="">-- No Sale Order --</option>
              {customerSaleOrders.map(o => (<option key={o.id} value={o.id}>{o.docNumber} - {o.partyName}</option>))}
            </Select>
          </FormGroup>
          <FormGroup $span2><Label>Customer *</Label>
            <CustomerSelect customers={customers} selectedCustomerId={formData.partyId} onChange={handleCustomerChange} disabled={!!formData.parentId} balanceOnDemand />
          </FormGroup>
          <FormGroup><Label>Customer PO #</Label><Input value={formData.customerPO || ''} onChange={e => handleHeaderChange('customerPO', e.target.value)} placeholder="Customer's PO number" /></FormGroup>
          <FormGroup><Label>Vehicle No</Label><Input value={formData.vehicleNo} onChange={e => handleHeaderChange('vehicleNo', e.target.value)} placeholder="Vehicle number" /></FormGroup>
          <FormGroup><Label>Driver Name</Label><Input value={formData.driverName} onChange={e => handleHeaderChange('driverName', e.target.value)} placeholder="Driver's name" /></FormGroup>
          <FormGroup><Label>Driver Phone</Label><Input value={formData.driverPhone} onChange={e => handleHeaderChange('driverPhone', e.target.value)} placeholder="Driver's phone" /></FormGroup>
          <FormGroup $span2><Label>Delivery Address</Label><Input value={formData.deliveryAddress} onChange={e => handleHeaderChange('deliveryAddress', e.target.value)} placeholder="Delivery location" /></FormGroup>
          <FormGroup>
            <Label>Employee</Label>
            <Select value={formData.employeeId} onChange={e => {
              const emp = employees.find(em => em.id === e.target.value);
              handleHeaderChange('employeeId', e.target.value);
              handleHeaderChange('employeeName', emp ? `${emp.firstName || ''} ${emp.lastName || ''}`.trim() : '');
            }}>
              <option value="">-- Select Employee --</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.employeeCode ? emp.employeeCode + ' - ' : ''}{emp.firstName} {emp.lastName}</option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>Job</Label>
            <Select value={formData.jobId} onChange={e => {
              const job = jobs.find(j => j.id === e.target.value);
              handleHeaderChange('jobId', e.target.value);
              handleHeaderChange('jobName', job?.jobName || '');
            }}>
              <option value="">-- Select Job --</option>
              {jobs.map(job => (
                <option key={job.id} value={job.id}>{job.jobCode ? job.jobCode + ' - ' : ''}{job.jobName}</option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>{reqLabel(activeCompany, 'From Location')}</Label>
            <Select value={formData.fromLocationId} onChange={e => {
              const loc = locations.find(l => l.id === e.target.value);
              handleHeaderChange('fromLocationId', e.target.value);
              handleHeaderChange('fromLocationName', loc?.locationName || '');
            }}>
              <option value="">-- Select Location --</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.locationName}</option>
              ))}
            </Select>
          </FormGroup>
        </FormGrid>
      </FormCard>
      <FormCard>
        <LineItemsHeader><SectionTitle style={{ margin: 0 }}>Line Items</SectionTitle>
          {!formData.parentId && <AddLineButton onClick={addLine}><FiPlus /> Add Row</AddLineButton>}
        </LineItemsHeader>
        <LineTableContainer><LineTable><thead><tr>
          <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}>#</LineTh><LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Item</LineTh><LineTh style={{ width: 260, minWidth: 260, maxWidth: 260 }}>Description</LineTh>
          {lineItems.some(l => l.hsCode) && <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>HS Code</LineTh>}
          <LineTh style={{ width: 120, minWidth: 120, maxWidth: 120 }}>Batch</LineTh><LineTh style={{ width: 110, minWidth: 110, maxWidth: 110 }}>Pack Size</LineTh>
          <LineTh style={{ width: 130, minWidth: 130, maxWidth: 130 }}>Ordered Qty</LineTh>
          {soCols && <LineTh style={{ width: 130, minWidth: 130, maxWidth: 130 }}>Already Delivered</LineTh>}
          {soCols && <LineTh style={{ width: 130, minWidth: 130, maxWidth: 130 }}>Remaining</LineTh>}
          <LineTh style={{ width: 130, minWidth: 130, maxWidth: 130 }}>Delivered Qty</LineTh>
          {lineItems.some(l => l.trackingType === 'Batch') && <LineTh style={{ width: 160, minWidth: 160, maxWidth: 160 }}>Batch</LineTh>}
          {lineItems.some(l => l.trackingType === 'Serial') && <LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Serial Numbers</LineTh>}
          <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}></LineTh>
        </tr></thead><tbody>
          {lineItems.map((line, index) => {
            const calc = calculateLine(line);
            const soP = soCols ? soProgress[line.soLineNumber] : null;
            const soRemaining = soP ? Math.max(0, r6(soP.ordered - soP.delivered)) : null;
            const soOver = soRemaining !== null && (Number(line.quantity) || 0) > soRemaining + 1e-6;
            return (<React.Fragment key={index}><tr>
              <LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>{index + 1}</LineTd>
              <LineTd><ItemSelect ref={(el) => { lineItemRefs.current[index] = el; }} items={filterItemsByPricing(items, customerPricing)} selectedItemId={line.itemId} onChange={(itemId) => handleLineChange(index, 'itemId', itemId)} disabled={!!formData.parentId} stockBalances={stockBalances} stockOnDemand onRequestStock={requestItemStock} /></LineTd>
              <LineTd>
                <LineInput value={line.description} onChange={e => handleLineChange(index, 'description', e.target.value)} placeholder="Description" disabled={!!formData.parentId} />
                
              </LineTd>
              {lineItems.some(l => l.hsCode) && <LineTd><LineInput value={line.hsCode || ''} onChange={e => handleLineChange(index, 'hsCode', e.target.value)} placeholder="HS Code" disabled={!!formData.parentId} /></LineTd>}
              <LineTd><LineInput value={line.batch || ''} onChange={e => handleLineChange(index, 'batch', e.target.value)} placeholder="Batch" /></LineTd>
              <LineTd><LineInput value={line.packSize || ''} onChange={e => handleLineChange(index, 'packSize', e.target.value)} placeholder="e.g. 12x500ml" /></LineTd>
              <LineTd><LineInput type="number" min="0" value={line.orderedQty} disabled style={{ textAlign: 'right', background: colors.neutral[100] }} /></LineTd>
              {soCols && <LineTd><LineInput type="number" value={soP ? soP.delivered : ''} placeholder="-" disabled style={{ textAlign: 'right', background: colors.neutral[100] }} /></LineTd>}
              {soCols && <LineTd><LineInput type="number" value={soRemaining === null ? '' : soRemaining} placeholder="-" disabled style={{ textAlign: 'right', background: colors.neutral[100] }} /></LineTd>}
              <LineTd>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <LineInput type="number" min="0" value={line.quantity}
                    onChange={e => handleLineChange(index, 'quantity', Number(e.target.value))}
                    onKeyDown={(e) => handleLastFieldEnter(e, index)}
                    disabled={line.hasSize}
                    title={line.hasSize ? 'Auto-computed from L × H × Pieces' : (soOver ? `More than the ${soRemaining} remaining on the Sale Order` : undefined)}
                    style={{ textAlign: 'right', background: line.hasSize ? '#F1F5F9' : undefined, color: soOver ? colors.error.main : undefined }} />
                  <LineUnitPicker
                    item={items.find(i => i.id === line.itemId)}
                    line={line}
                    onChange={(uc) => handleLineChange(index, 'unitCode', uc)}
                    disabled={!!formData.parentId}
                  />
                </div>
                <TwoUnitQtyBox item={items.find(i => i.id === line.itemId)} unitCode={line.unitCode || line.unitOfMeasure}
                  quantity={line.quantity} hasSize={line.hasSize}
                  onChange={(q) => handleLineChange(index, 'quantity', q)} />
              </LineTd>
              {lineItems.some(l => l.trackingType === 'Batch') && (
                <LineTd>{line.trackingType === 'Batch' ? (
                  <Select value={line.batchNumber} onChange={e => handleLineChange(index, 'batchNumber', e.target.value)} style={{ padding: '6px 8px', fontSize: '13px', minWidth: 120 }}>
                    <option value="">Select Batch</option>
                    {(availableBatches[line.itemId] || []).map(b => (
                      <option key={b.batchNumber} value={b.batchNumber}>{b.batchNumber} (Qty: {b.remaining}{b.expiryDate ? `, Exp: ${b.expiryDate}` : ''})</option>
                    ))}
                  </Select>
                ) : <span style={{ color: '#999' }}>-</span>}</LineTd>
              )}
              {lineItems.some(l => l.trackingType === 'Serial') && (
                <LineTd>{line.trackingType === 'Serial' ? (
                  <SerialSelectPopover
                    availableSerials={availableSerials[line.itemId] || []}
                    selectedSerials={line.serialNumbers || []}
                    requiredCount={line.quantity || 0}
                    onChange={(serials) => handleLineChange(index, 'serialNumbers', serials)}
                  />
                ) : <span style={{ color: '#999' }}>-</span>}</LineTd>
              )}
              <LineTd style={{ textAlign: 'center' }}>{!formData.parentId && lineItems.length > 1 && <RemoveLineBtn onClick={() => removeLine(index)} title="Remove"><FiX /></RemoveLineBtn>}</LineTd>
            </tr>
                    {line.hasSize && (<SizeLineRow line={line} index={index} onChange={handleLineChange} disabled={!!formData.parentId} leftColSpan={2} />)}
                    </React.Fragment>);
          })}
        </tbody></LineTable></LineTableContainer>
      </FormCard>
      <FormCard><SectionTitle>Additional Information</SectionTitle><FormGrid>
        <FormGroup $span2><Label>Notes</Label><TextArea value={formData.notes} onChange={e => handleHeaderChange('notes', e.target.value)} placeholder="Internal notes..." rows={3} /></FormGroup>
      </FormGrid></FormCard>

      <AttachmentPanel
        transactionId={editingChallan?.id}
        attachment={editingChallan?.attachment}
        canEdit={canEdit}
        onChange={({ attachment }) => setEditingChallan(prev => prev ? { ...prev, attachment } : prev)}
      />
    </>);
  };

  return (
    <PageLayout pageTitle="Delivery Challan" breadcrumb="Sales / Delivery Challan">
      {viewMode === 'list' && renderListView()}
      {viewMode === 'form' && renderFormView()}
      {viewMode === 'view' && renderViewMode()}
    </PageLayout>
  );
};

export default DeliveryChallan;
